import { Turnstile } from '@marsidev/react-turnstile';
import { Button } from '@material-ui/core';
import { ReloadableResultStatus } from 'api/resultStatus';
import {
  ApiError,
  ApplicantPortalService,
  DataFields,
  FileUpload,
  UnprocessableFields,
} from 'api-clients/monolith';
import { words } from 'lodash';
import React, { FormEvent, useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useHistory, useLocation } from 'react-router-dom';

import { ConsentCheckbox } from 'components/ConsentCheckbox/ConsentCheckbox';
import { Opening, OpeningProps } from 'components/Opening';
import { Typography } from 'components/Typography';
import { SubmitProps } from 'features/DataCollection/ProgressManager';
import { domainMeta } from 'utils/domainMeta';

import { ConsentVariant } from './apiResponse';
import { ApplicationFormBody } from './ApplicationFormBody';
import { ApplicationFormEmbeddedBody } from './ApplicationFormEmbeddedBody';
import { applicationFormQuestions } from './ApplicationFormQuestions';
import { postInternalApiPortalApplicationForms } from './applicationFormService';
import { consentMessage } from './ConsentMessage';
import {
  canReapplyMessages,
  DuplicateApplicantDataType,
  getLinkMessages,
} from './constants';
import { CreateReply } from './createReply';
import { ReachApplicationFormBody } from './ReachApplicationFormBody';
import { useStyles } from './styles';

export interface Opening extends OpeningProps {
  countryCode?: string;
}

const formName = (dataKey: string): string => `application_form[${dataKey}]`;

export interface ApplicationFormBodyRouteProps {
  className?: string;
  accountSlug: string;
  jobDescriptionHTML: string;
  opening: Opening;
  funnelId: string;
  viewAllJobs: boolean;
  hideJobWording: boolean;
  dataFields: DataFields;
  uploadFields?: FileUpload;
  setLaunchPortal: (url: string) => void;
  customSuccessMessage: string;
  isDspBrand?: boolean;
  amazonAccount?: boolean;
  displayLocale: string;
  smsConsentVariant: ConsentVariant;
  isIframe?: boolean;
  turnstileEnabled: boolean;
  isItcDemoEnabled: boolean;
  validateEmail: boolean;
  hideEmailField: boolean;
  requirements?: string[];
  reachApplicationFormEnabled: boolean;
  isExpandJobDescription: boolean;
  upsAccount?: boolean;
  showFullNameField: boolean;
  showCompensation: boolean;
  locationRequestEnabled: boolean;
  directoryPath: string;
}

export type SubmitStatus =
  | ReloadableResultStatus<CreateReply, UnprocessableFields>
  | { status: 'idle' };

// Plugs into ProgressManager to provide a customized submit button with some surrounding text
const submit =
  (
    smsConsentVariant: ConsentVariant,
    turnstileEnabled: boolean,
    accountSlug: string,
    isItcDemoEnabled: boolean,
    opening: Opening,
    locationRequestEnabled: boolean,
    upsAccount?: boolean,
    isIframe?: boolean,
  ): React.VFC<SubmitProps> =>
  ({ onClick }: SubmitProps) => {
    const styles = useStyles();
    const [showConsentError, setShowConsentError] = useState(false);
    const [showCallConsentError, setShowCallConsentError] = useState(false);
    const [consented, setConsented] = useState(false);
    const [callConsented, setCallConsented] = useState(false);
    const [location, setLocation] = useState<{
      latitude: number | undefined;
      longitude: number | undefined;
    }>({ latitude: undefined, longitude: undefined });

    const handleClick = (e: React.MouseEvent) => {
      setShowConsentError(!consented);
      setShowCallConsentError(!callConsented);
      onClick(e);
    };

    const handleConsent = (e: React.ChangeEvent<HTMLInputElement>) => {
      setConsented(e.target.checked);
      setShowConsentError(false);
    };

    const handleCallConsent = (e: React.ChangeEvent<HTMLInputElement>) => {
      setCallConsented(e.target.checked);
      setShowCallConsentError(false);
    };

    const linkToPrivacyPolicy = () => {
      return upsAccount
        ? 'https://www.jobs-ups.com/faqs-privacy'
        : 'https://web.fountain.com/privacy.html';
    };

    useEffect(() => {
      const success: PositionCallback = position => {
        const { latitude, longitude } = position.coords;
        setLocation({ latitude, longitude });
      };

      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(success, () => {}, {
          maximumAge: 60000,
          timeout: 10000,
        });
      }
    }, []);

    return (
      <div>
        {locationRequestEnabled && (
          <>
            <input
              type="hidden"
              name={formName('lat')}
              value={location?.latitude}
            />
            <input
              type="hidden"
              name={formName('long')}
              value={location?.longitude}
            />
          </>
        )}
        <ConsentCheckbox
          name={formName('sms_consented')}
          required
          error={showConsentError}
          label={consentMessage(
            isItcDemoEnabled ? 'ITC' : smsConsentVariant,
            opening.brandName,
          )}
          errorLabel={
            <FormattedMessage
              defaultMessage="Use of your phone number is required to open an application."
              description="Consent required field message"
            />
          }
          checked={consented}
          onChange={handleConsent}
        />
        <ConsentCheckbox
          name={formName('call_consented')}
          required
          error={showCallConsentError}
          label={
            <FormattedMessage
              defaultMessage="By checking this box, I agree to receive calls regarding my application or retainment."
              description="Consent to be called field message"
            />
          }
          errorLabel={
            <FormattedMessage
              defaultMessage="Use of your phone number for calls is required to open an application."
              description="Consent to be called required field message"
            />
          }
          checked={callConsented}
          onChange={handleCallConsent}
        />
        {turnstileEnabled && (
          <>
            <Turnstile
              siteKey={domainMeta.apiKey.turnstileSiteKey}
              options={{
                appearance: 'always',
                theme: 'light',
                responseFieldName: 'cf_turnstile_response',
                action: accountSlug.slice(0, 32), // 32 character limit
              }}
            />
            <Typography>
              <FormattedMessage
                defaultMessage="This page is protected by Cloudflare Turnstile."
                description="Notice to the user that Turnstile verification is enabled on the page."
              />
            </Typography>
          </>
        )}
        <Button
          type="submit"
          variant="contained"
          color="primary"
          onClick={handleClick}
          className={styles.button}
        >
          {isItcDemoEnabled ? (
            <FormattedMessage
              defaultMessage="Your future awaits"
              description="Continue button label for the application form"
            />
          ) : (
            <FormattedMessage
              defaultMessage="Continue"
              description="Continue button label for the application form"
            />
          )}
        </Button>
        <Typography variant="subtitleCompact" className={styles.noticeText}>
          <FormattedMessage
            defaultMessage="Your use of Fountain is governed by our
          <a>Privacy Policy</a> and <b>Terms of Service</b>."
            description="Notice text in application form"
            values={{
              a: (msg: string) => (
                <a
                  href={linkToPrivacyPolicy()}
                  target={isIframe ? '_blank' : undefined}
                  rel="noopener"
                >
                  {msg}
                </a>
              ),
              b: (msg: string) => (
                <a
                  href="https://web.fountain.com/tos.html"
                  target={isIframe ? '_blank' : undefined}
                  rel="noopener"
                >
                  {msg}
                </a>
              ),
            }}
          />
        </Typography>
      </div>
    );
  };

const submitErrorMessage = (validation?: UnprocessableFields): JSX.Element => {
  if (!validation) {
    return (
      <FormattedMessage
        defaultMessage="Submission failed. Please try again."
        description="Alert text that the application form failed to submit"
      />
    );
  }
  const firstKey = Object.keys(validation.errors)[0];
  switch (firstKey) {
    case 'email':
      return (
        <FormattedMessage
          defaultMessage="Please enter a valid email address."
          description="Alert that the user's email address appears to be invalid"
        />
      );
    case 'turnstile':
      return (
        <FormattedMessage
          defaultMessage="Captcha verification failed. Please retry or refresh the page."
          description="Alert that the turnstile verification errored"
        />
      );
    default:
      return (
        <FormattedMessage
          defaultMessage="We're having trouble with one of your responses, please review the form."
          description="Generic alert that the user provided invalid data"
        />
      );
  }
};

export const ApplicationFormBodyRoute: React.VFC<
  ApplicationFormBodyRouteProps
> = ({
  accountSlug,
  opening,
  funnelId,
  viewAllJobs,
  hideJobWording,
  setLaunchPortal,
  customSuccessMessage,
  isDspBrand,
  amazonAccount,
  displayLocale,
  smsConsentVariant,
  isIframe,
  turnstileEnabled,
  isItcDemoEnabled,
  validateEmail,
  hideEmailField,
  requirements,
  reachApplicationFormEnabled,
  upsAccount,
  showFullNameField,
  locationRequestEnabled,
  directoryPath,
  ...rest
}) => {
  const intl = useIntl();
  const [submitting, setSubmitting] = useState<SubmitStatus>({
    status: 'idle',
  });
  const [duplicateApplicantData, setDuplicateApplicantData] =
    useState<DuplicateApplicantDataType>();
  const [applicationFormData, setApplicationFormData] = useState<FormData>();
  const [canReapply, setCanReapply] = useState<boolean>();
  const history = useHistory();
  const { pathname, search } = useLocation();

  const referredByApplicantId = useMemo(() => {
    const params = new URLSearchParams(search);
    return params.get('referred_by_applicant_id') ?? undefined;
  }, [search]);

  const referralId = useMemo(() => {
    const params = new URLSearchParams(search);
    return params.get('referral_id') ?? undefined;
  }, [search]);

  const ApplicationFormQuestionsComponent = useMemo(
    () =>
      applicationFormQuestions(
        validateEmail,
        opening.countryCode,
        referredByApplicantId,
        referralId,
        hideEmailField,
        showFullNameField,
      ),
    [
      validateEmail,
      opening.countryCode,
      referredByApplicantId,
      referralId,
      hideEmailField,
      showFullNameField,
    ],
  );

  const Submit = useMemo(
    () =>
      submit(
        smsConsentVariant,
        turnstileEnabled,
        accountSlug,
        isItcDemoEnabled,
        opening,
        locationRequestEnabled,
        upsAccount,
        isIframe,
      ),
    [
      smsConsentVariant,
      turnstileEnabled,
      accountSlug,
      isItcDemoEnabled,
      opening,
      locationRequestEnabled,
      upsAccount,
      isIframe,
    ],
  );

  const handleStartApplication = () => {
    const nameInput = document.querySelector('input');
    if (!nameInput) {
      /* istanbul ignore next -- we will always have inputs, just making TS happy */
      throw new Error('Expected an input on the application form');
    }
    nameInput.focus();
    // jsdom doesn't support scrollIntoView
    if (typeof nameInput.scrollIntoView === 'function') {
      /* istanbul ignore next -- unreachable in jsdom */
      nameInput.scrollIntoView({ block: 'center' });
    }
  };

  const handleBackToTop = () => {
    window.scrollTo(0, 0);
  };

  const handleDuplicateApplicantModal = (
    canReapply?: boolean,
    applicantId?: string,
    duplicateMessage?: string,
  ) => {
    if (canReapply) {
      setDuplicateApplicantData({ ...canReapplyMessages(intl), applicantId });
    } else {
      setDuplicateApplicantData({
        ...getLinkMessages(intl),
        applicantId,
        duplicateMessage,
      });
    }
  };

  const handleSubmit = async (
    formData: FormData,
    event?: FormEvent<Element>,
  ) => {
    event?.preventDefault();
    if (submitting.status === 'loading') {
      return;
    }
    setSubmitting({
      isLoading: true,
      isError: false,
      status: 'loading',
    });

    try {
      formData.append('application_form[application_locale]', displayLocale);

      if (showFullNameField) {
        const fullName = formData.get('application_form[full_name]');
        const [firstName, ...otherNameParts] = words(fullName as string);
        formData.append('application_form[first_name]', firstName);
        formData.append(
          'application_form[last_name]',
          otherNameParts.join(' ') || '',
        );
      }

      const response = await postInternalApiPortalApplicationForms(
        accountSlug,
        funnelId,
        formData,
      );
      setSubmitting({ status: 'idle' });
      if (response.is_duplicate) {
        setCanReapply(response.can_reapply);
        handleDuplicateApplicantModal(
          response.can_reapply,
          response.applicant_external_id,
          response.duplicate_message,
        );
        if (response.can_reapply) {
          setApplicationFormData(formData);
        }
      } else if (response.applicant_external_id) {
        setDuplicateApplicantData(undefined);
        const applyLink = `/apply/${accountSlug}/applications/${response.applicant_external_id}`;

        if (
          isIframe &&
          response.launch_portal_from_form &&
          !customSuccessMessage.length
        ) {
          window.parent.location = applyLink;
        }

        if (response.launch_portal_from_form && !customSuccessMessage.length) {
          history.push(applyLink);
        } else {
          if (response.launch_portal_from_form) setLaunchPortal(applyLink);
          history.push(`${pathname.replace(/\/$/, '')}/complete`);
        }
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
      if (error instanceof ApiError && error.status === 422) {
        setSubmitting({
          isLoading: false,
          isError: true,
          status: 'error',
          error: error.body as UnprocessableFields,
        });
      } else {
        setSubmitting({
          isLoading: false,
          isError: true,
          status: 'error',
        });
      }
    }
  };

  const sendPreviousApplicationLink = async () => {
    try {
      if (duplicateApplicantData && duplicateApplicantData.applicantId) {
        await ApplicantPortalService.postInternalApiPortalApplicationsApplicationRequests(
          accountSlug,
          duplicateApplicantData.applicantId,
        );

        history.push(`${pathname}/linkSent`);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
      setSubmitting({
        isLoading: false,
        isError: true,
        status: 'error',
      });
    }
  };

  const handleReapply = async () => {
    if (applicationFormData) {
      // avoid checking for duplicate applicant again on re-submission
      applicationFormData.delete(
        'application_form[check_if_applicant_is_duplicate]',
      );
      applicationFormData.set('application_form[turnstile_verified]', 'true');
      await handleSubmit(applicationFormData);
    }
  };

  const handleDuplicateApplicantModalClose = () => {
    setDuplicateApplicantData(undefined);
  };

  const showAllJobsButton = (viewAllJobs && !amazonAccount) || isDspBrand;

  const FormBody = reachApplicationFormEnabled
    ? ReachApplicationFormBody
    : ApplicationFormBody;

  if (isIframe && !reachApplicationFormEnabled) {
    return (
      <ApplicationFormEmbeddedBody
        ApplicationFormQuestionsComponent={ApplicationFormQuestionsComponent}
        canReapply={Boolean(canReapply)}
        duplicateApplicantData={duplicateApplicantData}
        formName={formName}
        handleBackToTop={handleBackToTop}
        handleDuplicateApplicantModalClose={handleDuplicateApplicantModalClose}
        handleReapply={handleReapply}
        handleSubmit={handleSubmit}
        sendPreviousApplicationLink={sendPreviousApplicationLink}
        setSubmitting={setSubmitting}
        Submit={Submit}
        submitErrorMessage={submitErrorMessage}
        submitting={submitting}
        opening={opening}
        {...rest}
      />
    );
  }

  return (
    <FormBody
      ApplicationFormQuestionsComponent={ApplicationFormQuestionsComponent}
      canReapply={Boolean(canReapply)}
      duplicateApplicantData={duplicateApplicantData}
      formName={formName}
      handleBackToTop={handleBackToTop}
      handleDuplicateApplicantModalClose={handleDuplicateApplicantModalClose}
      handleReapply={handleReapply}
      handleStartApplication={handleStartApplication}
      handleSubmit={handleSubmit}
      sendPreviousApplicationLink={sendPreviousApplicationLink}
      setSubmitting={setSubmitting}
      showAllJobsButton={Boolean(showAllJobsButton)}
      hideJobWording={hideJobWording}
      Submit={Submit}
      submitErrorMessage={submitErrorMessage}
      submitting={submitting}
      opening={opening}
      accountSlug={accountSlug}
      isDspBrand={isDspBrand}
      isItcDemoEnabled={isItcDemoEnabled}
      requirements={requirements}
      directoryPath={directoryPath}
      {...rest}
    />
  );
};
